/**
 * AudienceCommentersTab — renders the audience_commenters block using
 * the same chart vocabulary as the Followers tab. Mounted as a 3rd tab
 * inside the existing Audience section (only when audience_commenters
 * has any non-null demographic field — many creators return all-null).
 *
 * V1: KPI-row summary. The full chart parity with the Followers tab is
 * tracked as a follow-up that requires extracting the existing inline
 * Audience charts into a reusable <AudienceCharts /> component first.
 */
import type { InsightIQProfile } from '@discovr/types';
import { fmtDecimal2 } from '../utils/formatters';

interface AudienceCommentersTabProps {
  commenters: NonNullable<InsightIQProfile['audience_commenters']>;
}

export function AudienceCommentersTab({ commenters }: AudienceCommentersTabProps) {
  const topCountry = commenters.countries?.[0];
  const topCountryPct = topCountry ? fmtDecimal2(topCountry.value) : null;
  const credPct = commenters.credibility_score != null
    ? Math.round(commenters.credibility_score * 100)
    : null;
  const genderSplit = computeGenderSplit(commenters.gender_age_distribution);

  return (
    <div className="space-y-4">
      {credPct != null && (
        <KpiRow label="Commenter credibility" value={`${credPct}%`} />
      )}
      {topCountry && topCountryPct != null && (
        <KpiRow
          label="Top commenter country"
          value={`${topCountry.code} · ${topCountryPct}%`}
        />
      )}
      {genderSplit && (
        <KpiRow
          label="Commenter gender mix"
          value={`♂ ${genderSplit.male}% · ♀ ${genderSplit.female}%`}
        />
      )}
      {(commenters.significant_commenters?.length ?? 0) > 0 && (
        <KpiRow
          label="Significant commenters"
          value={`${commenters.significant_commenters?.length ?? 0} notable accounts`}
        />
      )}
    </div>
  );
}

function KpiRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between border-b border-border pb-2 last:border-0">
      <span className="text-body-sm-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="text-body-sm-semibold text-foreground">{value}</span>
    </div>
  );
}

function computeGenderSplit(
  dist: Array<{ gender: string; age_range: string; value: number }> | undefined,
) {
  if (!dist || dist.length === 0) return null;
  let male = 0;
  let female = 0;
  for (const d of dist) {
    if (d.gender?.toUpperCase() === 'MALE') male += d.value;
    else if (d.gender?.toUpperCase() === 'FEMALE') female += d.value;
  }
  const total = male + female || 1;
  return {
    male: Math.round((male / total) * 100),
    female: Math.round((female / total) * 100),
  };
}
